"use client";

import { createContext, useState, useEffect, useRef, ReactNode, useMemo, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { newGuestKey } from "@/lib/carts";

export type WishlistItem = { slug: string; name: string; price: number; image: string };

type Ctx = {
  items: WishlistItem[];
  has: (slug: string) => boolean;
  toggle: (item: WishlistItem) => void;
  remove: (slug: string) => void;
  clear: () => void;
  count: number;
};

export const WishlistContext = createContext<Ctx | null>(null);

const STORAGE_KEY = "onomac-wishlist";

function writeLocal(items: WishlistItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const initialized = useRef(false);
  const restored = useRef(false);
  const everSynced = useRef(false);
  const { user, isLoading: authLoading } = useAuth();
  const userEmail = user?.email?.trim().toLowerCase() || null;

  const [guestId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      let g = localStorage.getItem("onomac-guest-id");
      if (!g) {
        g = newGuestKey();
        localStorage.setItem("onomac-guest-id", g);
      }
      return g;
    } catch {
      return null;
    }
  });

  // İlk yükleme: localStorage (ezme yok)
  useEffect(() => {
    if (initialized.current) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as WishlistItem[];
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (Array.isArray(parsed)) setItems(parsed.filter((i) => i.slug));
      }
    } catch {}
    initialized.current = true;
  }, []);

  // localStorage yaz (mount ezme race fix)
  useEffect(() => {
    if (!initialized.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as WishlistItem[];
          if (Array.isArray(parsed)) setItems(parsed.filter((i) => i.slug));
        } catch {}
      }
      if (e.key === "onomac-cart-owner" && e.newValue) {
        // owner değişince tekrar server'dan çek
        restored.current = false;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Server'dan geri yükle (cihazlar arası devam) — auth çözülmeden çalışmaz
  useEffect(() => {
    if (!initialized.current || restored.current || authLoading) return;
    restored.current = true;
    if (items.length > 0) return;
    const q = userEmail ? "" : `?ownerKey=${encodeURIComponent(guestId ?? "")}`;
    fetch(`/api/wishlist${q}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { success?: boolean; items?: unknown } | null) => {
        const rows = Array.isArray(d?.items) ? (d.items as WishlistItem[]) : [];
        const valid = rows.filter((i) => i && typeof i.slug === "string");
        if (valid.length > 0) {
          setItems(valid);
          writeLocal(valid);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, authLoading]);

  // Server'a yaz (debounced) — admin paneli buradan izler
  useEffect(() => {
    if (!initialized.current || !restored.current) return;
    if (items.length === 0 && !everSynced.current) return;
    const ownerKey = userEmail ?? guestId ?? "";
    if (!ownerKey) return;
    const t = setTimeout(() => {
      if (items.length > 0) everSynced.current = true;
      fetch("/api/wishlist", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerKey, items }),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [items, userEmail, guestId]);

  const has = useCallback((slug: string) => items.some((i) => i.slug === slug), [items]);
  const toggle = useCallback(
    (item: WishlistItem) =>
      setItems((prev) =>
        prev.some((p) => p.slug === item.slug) ? prev.filter((p) => p.slug !== item.slug) : [...prev, item]
      ),
    []
  );
  const remove = useCallback((slug: string) => setItems((prev) => prev.filter((p) => p.slug !== slug)), []);
  const clear = useCallback(() => setItems([]), []);
  const count = useMemo(() => items.length, [items]);
  const value = useMemo(() => ({ items, has, toggle, remove, clear, count }), [items, has, toggle, remove, clear, count]);

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}
